import i18next, { t } from "i18next";
import {
  Chat,
  GlobalState,
  OptionAction,
  GlobalActions,
  Messages,
  GlobalAction,
  GlobalActionType,
  OptionActionType,
  MessageAttachment,
  CustomGpt,
  SharedTargetContent,
} from "./types";
import React from "react";
import { createResponse } from "../service/openai";
import { toaster } from "../../components/ui/toaster";
import { getLoginUrl, isRetryableMessage } from "../utils/loginRetry";
import { buildInputFile } from "../utils/attachments";
import { v7 as uuidv7 } from "uuid";
import { createCustomGptFromShare } from "../service/customGptFromShare";


async function processImages(images: any[], opfs: FileSystemDirectoryHandle | null) {
  console.log("transform images: %o", images);
  return await Promise.all(
    images.map(async (image) => {
      console.log("sendMessage: image: %o", image);
      if (image.url) {
        return {
          type: "input_image",
          image_url: image.url,
          name: image.name,
          attachmentId: image.url.startsWith("opfs://") ? image.id : undefined,
          size: image.size,
          mimeType: image.type,
        };
      } else if ((image.opfsName || image.name) && opfs) {
        console.log("sendMessage: reading file: %o", image.name);

        // get file from OPFS
        const opfsName = image.opfsName || image.name;
        const fileHandle = await opfs.getFileHandle(opfsName);
        if (!fileHandle) {
          console.error("File not found in OPFS: %s", image.name);
          throw new Error(`File not found: ${image.name}`);
        }

        return {
          type: "input_image",
          image_url: `opfs://${opfsName}`,
          name: image.name,
          attachmentId: image.id,
          size: image.size,
          mimeType: image.type,
        };
      }
    })
  );
}

function processFiles(files: MessageAttachment[]) {
  console.log("transform files: %o", files);
  // The request builder resolves local or lazily restored files to base64
  // `file_data` per request, so they are not uploaded through the files API.
  return files
    .filter((file) => Boolean(file.name))
    .map((file) => buildInputFile(file));
}

export default function action(
  state: Partial<GlobalState>,
  dispatch: React.Dispatch<GlobalAction>
): GlobalActions {
  const setState = (payload: Partial<GlobalState> = {}) =>
    dispatch({
      type: GlobalActionType.SET_STATE,
      payload: { ...payload },
    });

  const setIs = (arg) => {
    const { is } = state;
    setState({ is: { ...is, ...arg } });
  };

  const startResponse = (globalState, parent) => {
    void createResponse(globalState, parent).catch((error) => {
      console.error("Could not prepare the chat request:", error);
      setIs({ thinking: false });
      toaster.create({
        title: t("error_occurred"),
        description: error instanceof Error ? error.message : String(error),
        duration: 6000,
        type: "error",
      });
    });
  };

  const setOptions = (optionAction: OptionAction) => {
    console.log("set options: ", optionAction.type, optionAction.data);
    if (
      optionAction.type === OptionActionType.GENERAL &&
      optionAction.data.language
    ) {
      i18next.changeLanguage(optionAction.data.language);
    }
    dispatch({
      type: GlobalActionType.SET_OPTIONS,
      payload: optionAction,
    });
  };

  const startChat = (
    chat: Chat[],
    currentChat: number,
    currentApp = state.currentApp
  ) =>
    dispatch({
      type: GlobalActionType.START_CHAT,
      payload: { chat, currentChat, currentApp },
    });

  const retryPendingMessage = () => {
    const { chat, currentChat } = state;
    // `at()` avoids dynamic computed member access on the chat array, which
    // static analysis flags as a generic object-injection sink.
    const messages = chat?.at(currentChat)?.messages;
    const lastMessage = messages?.at(-1);

    if (!isRetryableMessage(lastMessage)) {
      console.log("retryPendingMessage: last message is not from the user");
      return;
    }

    console.log("retryPendingMessage: resending last user message");
    setIs({ ...state.is, thinking: true, tool: null });
    startResponse({ ...state, setState, setIs, setOptions }, this);
  };

  const sendMessage = async () => {
    const { typeingMessage, chat, currentChat } = state;

    let opfs = null;

    try {
      opfs = await navigator.storage.getDirectory();
    } catch (error) {
      console.error("Error getting OPFS directory: %o", error);
    }

    if (typeingMessage?.content) {
      let newMessage = {
        ...typeingMessage,
        role: "user",
        sentTime: Math.floor(Date.now() / 1000),
        id: uuidv7(),
      };

      if (
        (typeingMessage.images && typeingMessage.images.length > 0) ||
        (typeingMessage.files && typeingMessage.files.length > 0)
      ) {
        const images = typeingMessage.images?.length
          ? await processImages(typeingMessage.images, opfs)
          : [];
        const files = typeingMessage.files?.length
          ? processFiles(typeingMessage.files)
          : [];
        console.log("sendMessage: images: %o files: %o", images, files);

        newMessage.content = [
          { type: "input_text", text: typeingMessage.content },
          ...images,
          ...files,
        ];
      }
      let messages: Messages = [];
      console.log("sendMessage", chat);
      if (chat[currentChat]?.messages) {
        messages = [...chat[currentChat].messages];
      }
      messages.push(newMessage);
      const newChat = [...chat];
      newChat.splice(currentChat, 1, { ...chat[currentChat], messages });
      setState({
        chat: newChat,
        typeingMessage: {},
        is: { ...state.is, thinking: true, tool: null },
      });
      startResponse({ ...state, chat: newChat, setState, setIs, setOptions }, this);
    }
  };

  return {
    setState,
    setIs,
    doLogin(): void {
      console.log("doLogin");
      globalThis.location.href = getLoginUrl();
    },
    clearTypeing() {
      console.log("clear");
      setState({ typeingMessage: {} });
    },

    sendMessage,

    retryPendingMessage,

    async startCustomGptFromShare(
      customGpt: CustomGpt,
      shared: SharedTargetContent
    ) {
      return createCustomGptFromShare({
        state,
        customGpt,
        shared,
        setState,
        setIs,
        setOptions,
        startResponse,
        parent: this,
      });
    },

    setApp(app) {
      console.log("setApp", app);
      setState({ currentApp: app });
    },

    showSettings() {
      const { mode, assistant } = state.options.openai;
      if (mode !== "assistant") {
        console.log("no settings for %s", mode);
        return;
      }
      console.log("showSettings: %s", assistant);

      setState({
        is: {
          ...state.is,
          config: true,
        },
        currentEditor: {
          assistant,
          type: "assistant",
        },
      });
    },

    newChat(app) {
      const { currentApp, currentChat, chat } = state;
      const newApp = app || currentApp;
      console.log(
        "newChat: ",
        currentApp,
        newApp,
        chat,
        newApp?.title,
        newApp?.role
      );
      const messages: Messages = [
        {
          content: newApp?.content || t("system_welcome"),
          sentTime: Math.floor(Date.now() / 1000),
          role: newApp.role,
          id: uuidv7(),
        },
      ];

      const chatList = [
        {
          title: newApp?.title || t("new_conversation"),
          id: uuidv7(),
          app: newApp.id,
          messages,
          ct: Date.now(),
          botStarts: newApp.botStarts,
        },
        ...chat,
      ];
      const _chat: Chat[] = chatList;
      if (newApp.botStarts) {
        console.log("botStarts");
        const nextIs = { ...state.is, thinking: true, tool: null };
        setState({
          chat: _chat,
          currentChat: 0,
          currentApp: newApp,
          is: nextIs,
        });
        startResponse(
          {
            ...state,
            chat: _chat,
            currentChat: 0,
            currentApp: newApp,
            is: nextIs,
            setState,
            setIs,
            setOptions,
          },
          this
        );
      } else {
        console.debug("starting chat: %o", _chat);
        startChat(_chat, 0, newApp);
      }
    },

    modifyChat(arg, index) {
      const chat = [...state.chat];
      chat.splice(index, 1, { ...chat[index], ...arg });
      setState({ chat, currentEditor: null });
    },

    editChat(index, title) {
      const chat = [...state.chat];
      const _chat = { ...chat[index], title };
      chat.splice(index, 1, _chat);
      setState({
        chat,
      });
    },

    removeChat(index) {
      const chat = [...state.chat];
      chat.splice(index, 1);
      const payload =
        state.currentChat === index && index > 0
          ? { chat, currentChat: index - 1 }
          : { chat };
      console.log("removeChat: %o", payload);
      setState(payload);
    },

    setMessage(content) {
      const typeingMessage = {
        ...state.typeingMessage,
        role: "user",
        id: uuidv7(),
      };
      typeingMessage.content = content;
      dispatch({
        type: GlobalActionType.CHANGE_MESSAGE,
        payload: { is: { ...state.is, typeing: true }, typeingMessage },
      });
    },

    downloadThread(format = "json") {
      const chat = state.chat[state.currentChat];
      const messages = chat.messages;

      if (format === "markdown") {
        const content = messages
          .map((m) => {
            return `${m.role === "assistant" ? "## Assistant\n" : "## User\n"}${m.content
              }\n\n`;
          })
          .join("\n");
        const blob = new Blob([content], { type: "text/markdown" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `chat_${chat.id}.md`;
        a.click();
      } else if (format === "json") {
        const content = JSON.stringify(messages, null, 2);
        const blob = new Blob([content], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `chat_${chat.id}.json`;
        a.click();
      }
    },

    removeMessage(id) {
      console.log("removeMessage", id);
      const messages = state.chat[state.currentChat].messages.filter(
        (m) => m.id !== id
      );
      const chat = [...state.chat];
      chat[state.currentChat].messages = messages;
      setState({
        chat,
      });
    },

    editMessage(id) {
      console.log("editMessage", id);
      const message = state.chat[state.currentChat].messages.find(
        (m) => m.id == id
      );
      console.log("editMessage: original", message);
      const newMessage = { ...message, id: uuidv7() };

      if (Array.isArray(message.content)) {
        let textContent = "";
        const images = [];
        const files = [];

        for (const item of message.content) {
          if (item.type === "input_text") {
            textContent += item.text;
          } else if (item.type === "input_image") {
            const imageUrl = item.image_url;
            let name = item.name || "image.png";
            if (imageUrl?.startsWith("opfs://")) {
              name = item.name || imageUrl.replace("opfs://", "");
            }
            images.push({
              name: name,
              opfsName: imageUrl?.startsWith("opfs://")
                ? imageUrl.slice("opfs://".length)
                : name,
              url: imageUrl,
              // We don't have size/type/lastModified here, but MessageInput should handle it gracefully or we can mock it
              size: 0,
              type: "image/png",
              lastModified: Date.now(),
              id: item.attachmentId,
            });
          } else if (item.type === "input_file") {
            const name = item.filename || "document";
            files.push({
              name,
              opfsName: item.opfsName || name,
              url: `opfs://${item.opfsName || name}`,
              size: 0,
              type: getInputFileMimeType(name),
              lastModified: Date.now(),
              id: item.attachmentId,
            });
          }
        }
        newMessage.content = textContent;
        newMessage.images = images;
        newMessage.files = files;
      }

      console.log("editMessage: new", newMessage);
      setState({
        typeingMessage: newMessage,
      });
    },

    setOptions,

    currentList() {
      return state.chat[state.currentChat];
    },

    stopResponse() {
      setState({
        is: { ...state.is, thinking: false },
      });
    },
  };
}
